/**
 * The header's search (D-086): "/" focuses it from anywhere that is not a
 * field, the hits arrive grouped by kind as the person types (debounced,
 * the last request aborted, from two characters), each group as its kind
 * answers, a spinner in the field while what shows is older than the text;
 * the arrow keys walk them, Enter opens the picked one or the first kind's
 * "See all" page, Escape closes. A combobox over a listbox, so a screen
 * reader hears the picked hit. On a narrow screen (D-215) there is no
 * keyboard to press "/" on, so the field says only "Search".
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useNarrow } from '../lib/narrow.js';
import { addGroup, isSearchable, isTypingTarget, moveSelection, normalizeQuery, SEARCH_KINDS, searchEach, type SearchHit, type ShownSearch } from '../lib/search.js';
import { Icon } from './Icon.js';

const DEBOUNCE_MS = 200;

export function SearchBox() {
  const navigate = useNavigate();
  const narrow = useNarrow();
  const [raw, setRaw] = useState('');
  const [open, setOpen] = useState(false);
  const [shown, setShown] = useState<ShownSearch | null>(null);
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(-1);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const query = normalizeQuery(raw);
  const searchable = isSearchable(query);
  const groups = shown?.groups ?? null;

  // "/" from anywhere that is not already a field.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey || isTypingTarget(e.target)) return;
      e.preventDefault();
      inputRef.current?.focus();
      inputRef.current?.select();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Outside click closes.
  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  // The query, debounced; the previous request aborted. Busy from the keystroke, so the spinner says
  // at once that what shows answers older text; each kind's group lands as it answers.
  useEffect(() => {
    if (!isSearchable(query)) {
      setShown(null);
      setBusy(false);
      return;
    }
    setBusy(true);
    const controller = new AbortController();
    const timer = setTimeout(() => {
      searchEach(query, controller.signal, (group) => {
        setShown((s) => addGroup(s, query, group));
        setActive(-1);
        setOpen(true);
      })
        .catch(() => undefined) // aborted: the next query is already on its way
        .finally(() => {
          if (!controller.signal.aborted) setBusy(false);
        });
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query]);

  const hits: readonly SearchHit[] = groups?.flatMap((g) => g.hits) ?? [];
  const go = (href: string) => {
    setOpen(false);
    navigate(href);
  };
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      setActive((a) => moveSelection(a, e.key === 'ArrowDown' ? 1 : -1, hits.length));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const picked = active >= 0 ? hits[active] : undefined;
      if (picked) go(picked.href);
      else if (searchable && SEARCH_KINDS[0]) go(SEARCH_KINDS[0].allHref(query));
    } else if (e.key === 'Escape') {
      if (open) setOpen(false);
      else setRaw('');
    }
  };

  const showing = open && searchable;
  const nothing = groups !== null && hits.length === 0 && !busy;
  const withHits = groups?.filter((g) => g.hits.length > 0) ?? [];
  const failed = groups?.filter((g) => g.failed) ?? [];
  let index = -1;

  return (
    <div
      className={busy ? 'search search--busy' : 'search'}
      ref={boxRef}
      onBlur={(e) => {
        // Focus left the box by keyboard (to the gear, say): the popover goes with it.
        if (!boxRef.current?.contains(e.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      <Icon name="search" className="search__icon" />
      <input
        ref={inputRef}
        className="field search__field"
        type="search"
        placeholder={narrow ? 'Search' : 'Press / to search'}
        aria-label="Search"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={showing}
        aria-controls="search-results"
        aria-autocomplete="list"
        aria-activedescendant={active >= 0 ? `search-hit-${active}` : undefined}
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
        onFocus={() => groups && setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {!raw && <kbd className="search__hint">/</kbd>}
      {busy && searchable && <span className="spinner search__busy" aria-hidden="true" />}
      {showing && (
        <div className="search__pop">
          <div role="status" className={busy && groups === null ? 'search__empty' : nothing ? 'search__empty' : failed.length > 0 ? 'search__empty text-bad' : 'search__status'}>
            {busy && groups === null && 'Searching…'}
            {nothing && 'No matches.'}
            {failed.map((g) => `Could not search ${g.label.toLowerCase()}. `)}
          </div>
          {/* Only options and groups live in the listbox, so a screen reader counts the hits and nothing else. */}
          <div id="search-results" role="listbox" aria-label="Search results" aria-busy={busy}>
            {withHits.map((g) => (
              <div key={g.kind} className="search__group" role="group" aria-label={g.label}>
                <div className="search__label" aria-hidden="true">
                  {g.label}
                </div>
                {g.hits.map((h) => {
                  index += 1;
                  const i = index;
                  return (
                    <button
                      key={`${h.kind}-${h.id}`}
                      type="button"
                      id={`search-hit-${i}`}
                      role="option"
                      aria-selected={active === i}
                      className={`search__hit${active === i ? ' search__hit--active' : ''}`}
                      onMouseEnter={() => setActive(i)}
                      onClick={() => go(h.href)}
                    >
                      <span className="search__title">{h.title}</span>
                      {h.subtitle && <span className="search__sub">{h.subtitle}</span>}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
          {withHits.map((g) => (
            <Link key={g.kind} className="search__all" to={g.allHref} onClick={() => setOpen(false)}>
              See all {g.label.toLowerCase()} for “{query}”
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
